import {
  ChatCircleIcon,
  DotsThreeIcon,
  HeartIcon,
  PaperPlaneTiltIcon,
  RepeatIcon,
  SealCheckIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PreviewAvatar } from './PreviewAvatar';
import {
  DEFAULT_PREVIEW_BRAND_COLOR,
  type PlatformPreviewBaseProps,
  compactNumber,
  previewTimestampLabel,
  resolveAspectRatio,
  resolveCarouselAspectRatio,
} from './types';
import { useCaptionFold } from './useCaptionFold';
import { useCarousel } from './useCarousel';

import { cn } from '@/lib/utils';

// Threads doesn't publish an exact feed fold length; this is tuned to land at
// roughly the same visual cutoff the previous line-clamp-6 produced at this
// card's default width (~480px), scaled from LinkedIn's 210-char/~4-line fold.
const FOLD_LENGTH = 315;

/**
 * Threads post — Instagram's text-first feed sibling. Header is left-rail
 * avatar + name + handle + time, body text below, optional photo, action
 * row (heart, comment, repost, share).
 */
export const ThreadsPost: React.FC<PlatformPreviewBaseProps> = ({
  brandName,
  username,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  isVerified = false,
  caption,
  imageUrl,
  imageUrls,
  imageAspectRatios,
  mediaItems,
  carouselActiveIndex,
  onCarouselActiveIndexChange,
  videoUrl,
  videoPoster,
  aspectRatio,
  focalX,
  focalY,
  zoom,
  timestamp,
  engagement,
  isLoading = false,
  mediaOverlay,
  className,
}) => {
  const handle = (username || brandName).toLowerCase().replace(/\s+/g, '');
  const time = previewTimestampLabel(timestamp);
  const { currentImage, currentVideoUrl, currentVideoPoster, carouselOverlay, safeActive } =
    useCarousel(imageUrls, imageUrl, {
      activeIndex: carouselActiveIndex,
      onActiveIndexChange: onCarouselActiveIndexChange,
      mediaItems,
    });
  const hasMedia = !!currentImage || !!currentVideoUrl || !!videoUrl || isLoading;
  const resolvedAspect = resolveAspectRatio(
    resolveCarouselAspectRatio(aspectRatio, imageAspectRatios, safeActive),
    1,
  );
  const { visible, needsFold, expanded, setExpanded } = useCaptionFold(caption, FOLD_LENGTH);
  const likes = engagement?.likes;
  const comments = engagement?.comments;
  const reposts = engagement?.shares;

  return (
    <article
      className={cn('w-full bg-card border border-border/60 rounded-xl overflow-hidden', className)}
      data-platform="threads-post"
    >
      <div className="flex gap-3 p-3">
        <PreviewAvatar
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          className="size-10 rounded-full"
          textClassName="text-caption font-semibold"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{handle}</p>
            {isVerified ? (
              <SealCheckIcon className="size-3.5 shrink-0 text-foreground/85" weight="fill" />
            ) : null}
            <span className="ml-auto text-detail text-muted-foreground shrink-0">{time}</span>
            <DotsThreeIcon className="size-5 text-foreground/60 shrink-0" weight="bold" />
          </div>

          {caption ? (
            <p className="mt-0.5 text-sm leading-relaxed text-foreground whitespace-pre-wrap break-words">
              {visible}
              {needsFold && !expanded ? (
                <>
                  {'… '}
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="text-muted-foreground hover:text-foreground hover:underline transition-colors"
                  >
                    Show more
                  </button>
                </>
              ) : null}
            </p>
          ) : null}

          {hasMedia ? (
            <MediaSlot
              ratio={resolvedAspect}
              bg="muted"
              imageUrl={currentImage}
              videoUrl={currentVideoUrl ?? videoUrl}
              videoPoster={currentVideoPoster ?? videoPoster}
              focalX={focalX}
              focalY={focalY}
              zoom={zoom}
              isLoading={isLoading}
              alt={caption ?? `${brandName} post`}
              overlay={
                mediaOverlay ? (
                  <>
                    {carouselOverlay}
                    {mediaOverlay}
                  </>
                ) : (
                  carouselOverlay
                )
              }
              className="mt-2 rounded-2xl overflow-hidden border border-border/60"
            />
          ) : null}

          <div className="mt-2 flex items-center gap-1.5 -ml-1.5">
            <ThAction
              icon={<HeartIcon className="size-5" weight="regular" />}
              label={likes == null ? undefined : compactNumber(likes)}
            />
            <ThAction
              icon={<ChatCircleIcon className="size-5" weight="regular" />}
              label={comments == null ? undefined : compactNumber(comments)}
            />
            <ThAction
              icon={<RepeatIcon className="size-5" weight="regular" />}
              label={reposts == null ? undefined : compactNumber(reposts)}
            />
            <ThAction
              icon={<PaperPlaneTiltIcon className="size-5" weight="regular" />}
              label="Share"
            />
          </div>
        </div>
      </div>
    </article>
  );
};

const ThAction: React.FC<{ icon: React.ReactNode; label?: string }> = ({ icon, label }) => (
  <button
    type="button"
    className="flex items-center gap-1.5 rounded-full px-2 py-1.5 text-detail text-foreground/70 hover:bg-muted/50 hover:text-foreground transition-colors"
  >
    {icon}
    {label != null ? <span>{label}</span> : null}
  </button>
);
